/* eslint-disable */
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import React, { Component, useMemo } from 'react';

import { arrayMove } from 'react-sortable-hoc';
import { Sortable, SortableHandle, SortableItem } from '~/native-ui/sortable/Sortable';

import { Box, Center, HStack, View } from '~/native-ui/native-base';
import { Pressable } from '~/native-ui/Pressable';

import { Icon, IconButton } from '~/native-ui/Icon';
import { Button } from '~/native-ui/Button';
import { LinkToSpan } from '~/native-ui/Link';
import { FlatList } from '~/native-ui/List';
import { SpinnerInput as Input, Spinner } from '~/native-ui/Spinner';
import { HighLightText, Text } from '~/native-ui/Typography';
import { extractNativeProps, renderHTML } from '~/native-ui/utils';
import { DropdownOption } from './DropdownOption';


const editIconProps = {
    size: '4'
}

const CellRendererComponent = React.memo(({ item, children, style, onLayout }) => {
    const viewStyle = useMemo(() => [
        style,
        item.containerProps, 
        item.sticky ? { bottom: 0, top: 0, position: 'sticky' } : {}
    ], [style, item.sticky])
    return (
        <View onLayout={onLayout} style={viewStyle}>{children}</View>
    )
})

/**
 * @typedef SourceRecordObject
 * @type {object}
 * @property {string} text - the label for the record.
 * @property {string | number} value - value of the record.
 * @property {boolean} [disabled=false] - is the record not selectable
 */


const renderHtmlAsReact = (text, searchTerm) =>
    typeof text !== 'string'
        ? text
        : renderHTML(text, {
              a: LinkToSpan,
              p: (props) => props.children ?? null,
              Text: (props) => (
                  <Text pointerEvents='none'>
                      <HighLightText {...props}  pointerEvents='none' searchTerm={searchTerm} />
                  </Text>
              ),
          });

const CenteredSpinner = () => (
    <Center height="34px" width="34px">
        <Spinner size="xs" height="34px" />
    </Center>
);

const IconWrapper = (props) => {
    return <Center {...props} />;
};

const listItemFactory = (
    Input,
    parentProps,
    inputProps,
    { handleClick, renderIcons, renderTemplateValue }
) => {
    const { theme, template, labelKey, required, disabled, isReadOnly } = parentProps;
    return ({ value: selected, index }) => {
        const icons = renderIcons(selected, index, {
            ...(
                parentProps.multiSelect
                ? { iconAdd:  "add" }
                : { iconRemove: "clear" }
            ),
        });

        const textProps = {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        };

        const renderValues =
            template && selected ? (
                renderTemplateValue(selected, { disabled: disabled, onPress: disabled ? null : handleClick })
            ) : selected ? (
                <Text {...textProps}>{selected[labelKey]}</Text>
            ) : parentProps.placeholder ? (
                <Text {...textProps} color="muted.400">
                    {parentProps.placeholder}
                </Text>
            ) : null;

        return (
            <Pressable
                flexDirection="row"
                alignItems="center"
                onPress={disabled ? null : handleClick}
                backgroundColor={disabled ? '#f5f5f5' : undefined}
                cursor={disabled ? 'not-allowed' : undefined}
            >
                <Box paddingTop="2" paddingBottom="2" flex="1" {...textProps}>
                    {renderValues}
                </Box>
                {disabled || isReadOnly || (
                    <IconWrapper
                        ml="auto"
                        position="sticky"
                        right="0"
                        flexGrow={0}
                        flexShrink={0}
                        alignSelf="flex-start"
                    >
                        {icons}
                    </IconWrapper>
                )}
            </Pressable>
        );
    };
};

export default class SelectCore extends Component {
    /**
     * @property {'onclose' | 'onchange'} multiSelectModel - the model to use for multi select
     */
    multiSelectModel = "onchange";
    static propTypes = {
        orderable: PropTypes.bool,
        multiSelect: PropTypes.bool,
        allowBlank: PropTypes.bool,
        allowFiltering: PropTypes.bool,
        allowClearing: PropTypes.bool,
        minQueryLength: PropTypes.number,
        auto: PropTypes.bool,
        className: PropTypes.string,
        disabled: PropTypes.bool,
        error: PropTypes.string,
        label: PropTypes.string,
        labelKey: PropTypes.string,
        name: PropTypes.string,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        onClick: PropTypes.func,
        onFocus: PropTypes.func,
        onQueryChange: PropTypes.func,
        required: PropTypes.bool,
        source: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
        template: PropTypes.func,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
        valueKey: PropTypes.string,
    };

    static defaultProps = {
        auto: true,
        className: '',
        allowBlank: true,
        disabled: false,
        labelKey: 'label',
        required: false,
        valueKey: 'value',
        minCharacters: 0,
        multiSelect: false,
        orderable: false,
    };

    state = {
        active: false,
        up: false,
        minWidth: 0,
        query: '',
        source: [],
        focusedRecordIndex: -1,
        dirtySelection: { items: [], ids: [] },
    };

    controlledFilter = !!this.props.allowFiltering && !!this.props.onQueryChange;

    selectionsFlatListElem = React.createRef(null);
    optionsFlatListElem = React.createRef(null);
    filterInputElem = React.createRef(null);

    /**
     * @returns {SourceRecordObject[]}
     */
    get source() {
        return this.controlledFilter ? this.props.source : this.state.source;
    }

    get query() {
        return this.controlledFilter ? this.props.query : this.state.query;
    }

    get filteredSource() {
        return this.source.filter((item) => {
            return this.props.value.indexOf(item[this.props.valueKey]) < 0;
        });
    }
    
    componentDidMount() {
        if (!this.controlledFilter) this.onQueryChange('');
    }

    componentDidCatch(error, errorInfo) {
        // You can also log the error to an error reporting service
        console.log(error, errorInfo);
    }

    extractProps() {
        return extractNativeProps(this.props);
    }

    /**
     * 
     * @param {FlatList} node 
     */
    setOptionsFlatListRef = (node) => {
        if (!node) this.removeEventListeners();
        this.optionsFlatListElem.current = node;
        this.attachEventListeners();
    }

    isSelected = (item) => {
        return this.state.dirtySelection.ids.includes(item[this.props.valueKey])
            || this.props.value.includes(item[this.props.valueKey]);
    }

    isInternal = (item) => {
        return (get(item, this.props.valueKey) + "").startsWith('system_');
    }


    getSelectedItem = () => {
        for (const item of this.props.source) {
            if (item[this.props.valueKey] === this.props.value) return item;
        }

        return !this.props.allowBlank ? this.props.source[0] : undefined;
    };

    getSelections = (includeDirty = false) => {
        let valueKey = this.props.valueKey;

        if (!this.props.multiSelect) {
            return [this.getSelectedItem()];
        }

        const itemsByKey = keyBy(this.props.source, valueKey);
        const selection = this.props.value;

        let items = Array.from(new Set(selection))
            .map((value) => itemsByKey[value])
            .filter(Boolean)
            .concat(includeDirty ? this.state.dirtySelection.items : []);

        if (!items.length) {
            items = [undefined];
        }

        return uniqBy(items, valueKey);
    };



    /**
     * 
     * @param {unknown} val 
     * @returns {unknown | unknown[]}
     */
    normalizeValueOnSelect (val, initialValue = this.props.value) {
        if (this.props.multiSelect) {
            if (initialValue.includes(val)) val = initialValue.filter((v) => v !== val);
            else val = [...initialValue, val].filter((v) => typeof v !== 'undefined');
        }

        return val;
    }    

    /**
     * 
     * @param {SourceRecordObject} val 
     * @param {MouseEvent | KeyboardEvent} event 
     * @returns 
     */
    handleSelect = (val, event) => {
        if (this.props.onBlur) this.props.onBlur(event);
        if (this.isDisabled() || !this.props.onChange) {
            return;
        }

        if ('system_add_new_record' === val) {
            this.onAddNewRecord();
            return;
        }

        if (this.props.name) event.target.name = this.props.name;
        

        if (this.props.multiSelect && this.multiSelectModel === 'onclose') {
            const ids = this.normalizeValueOnSelect(val, this.state.dirtySelection.ids);
            const itemsByKey = keyBy(this.state.dirtySelection.items.concat(this.props.source), this.props.valueKey);
            const items = ids.map((id) => itemsByKey[id]);

            this.setState({ dirtySelection: { items, ids } });
            return;
        }

        this.props.onChange(this.normalizeValueOnSelect(val), event);
        if (!this.props.multiSelect) this.close('element selected');
        return;

    };

    handleClick = (event) => {
        if (this.props.disabled) return;
        this.open(event);
        event.preventDefault();
        this.props.onClick?.(event);
    };

    close = (reason) => {
        if (!this.state.active) return;

        if (this.props.multiSelect && this.multiSelectModel === 'onclose') {
            const valueKey = this.props.valueKey;
            const selection = Array.from(
                new Set(
                    this.getSelections(true)
                        .filter(Boolean)
                        .map((item) => get(item, valueKey))
                )
            );

            this.props.onChange(selection);
        }
        
        //console.log('dropdown closing because of ' + reason)

        this.setState({ active: false, dirtySelection: { items: [], ids: [] } });

        if (reason !== "adding_new_record") this.onQueryChange("");
    };

    open = (event) => {
        if (this.state.active) return;
        const client = event.target.getBoundingClientRect();
        const screenHeight = window.innerHeight || document.documentElement.offsetHeight;
        const up = this.props.auto ? client.top > screenHeight / 2 + client.height : false;
        this.setState({ active: true, up, focusedRecordIndex: -1 });
    };

    attachEventListeners = () => {
        this.removeEventListeners();
        if (!this.optionsFlatListElem.current) return;
        window.addEventListener('keydown', this.onKeyboardEvent, true);
        this.optionsFlatListElem.current.getScrollableNode().addEventListener('mouseleave', this.handleMouseOut)
    }

    removeEventListeners () {
        window.removeEventListener('keydown', this.onKeyboardEvent, true);
        this.optionsFlatListElem.current?.getScrollableNode().removeEventListener('mouseleave', this.handleMouseOut)
    }

    componentWillUnmount() {
        this.removeEventListeners()
    }

    handleFocus = (event) => {
        event.preventDefault();
        if (!this.isDisabled()) this.open(event);
        this.props.onFocus?.(event);
    };

    handleBlur = (event) => {
        event.preventDefault();
        if (this.state.active) this.close('blur');
        this.props.onBlur?.(event);
    };

    renderTemplateValue = (selected, props) => {
        return <Pressable onPress={this.handleClick}>{this.props.template(selected, props)}</Pressable>;
    }

    handleMouseOut = () => {
        this.setNextFocusedRecordIndex(null);
    }

    onQueryChange = (query) => {
        if (this.controlledFilter) {
            this.props.onQueryChange(query);
            this.setState({
                focusedRecordIndex: -1,
            });
            return;
        }

        let { labelKey, source } = this.props,
            regex = new RegExp(query, 'ig');

        this.setState({
            query,
            source: source.filter((item) => regex.test(item[labelKey])),
            focusedRecordIndex: -1,
        });
    };

    setSearchFilterRef = (input) => {
        this.filterInputElem = input;
    };
    
    getIconLeft = () => {

        return (
            <Center height={10} width={10}>
                <Icon size="sm" type="search" iconFamily="material" />
            </Center>
        )
    }

    renderFilterInput = () => {
        const { loading } = this.props;

        return (
            <>
                <Input
                    loading={loading}
                    defaultValue={this.query}
                    ref={this.setSearchFilterRef}
                    px="3"
                    pl="0"
                    iconLeft={this.getIconLeft()}
                    placeholder={get(this.props.query, 'length', 0) ? '' : this.props.filterHint}
                    onChange={this.onQueryChange}
                />
            </>
        );
    };

    unselectItem = (item) => {
        if (this.props.disabled) return;
        
        let value = null;

        if (this.props.multiSelect) {
            value = this.props.value.filter((i) => i !== item?.value);
        }

        this.props.onChange(value);
    };

    isValueEmpty() {
        let { value, multiSelect } = this.props;

        if (multiSelect) {
            return value.filter((v) => typeof v !== 'undefined').length < 1;
        }

        return value === null || value === '';
    }

    /**
     * @typedef IconConfig
     * @type {object}
     * @property {string} [iconNotEmpty]
     * @property {string} [iconRemove]
     * @property {string} [iconDragIndicator]
     * @property {isLoading} [isLoading]
     * @property {object} [iconProps]
     * @property {object} [wrapperProps]

     */

    /**
     * 
     * @param {SourceRecordObject} item 
     * @param {number} i 
     * @param {IconConfig} iconConfiguration
     * @returns 
     */
    renderIcons = (item, i, {
        iconNotEmpty = 'keyboard-arrow-down',
        iconRemove = 'remove',
        iconAdd = iconNotEmpty,
        iconDragIndicator = 'drag-indicator',
        isLoading = this.props.loading,
        orderable = this.props.orderable,
        triggerSelectOnIconClick = true,
        canEdit = true,
        iconProps = {},
        wrapperProps = {},
    } = {}) => {
        let icons = [];

        if (isLoading && i === 0) {
            icons.push(<CenteredSpinner key="spinner" />);
            return icons;
        }

        if (this.props.disabled) {
            if (!icons.length) {
                icons.push(
                    !(typeof iconRemove === 'string')
                        ? iconRemove
                        :(
                            <IconButton
                                colorScheme="muted"
                                pointerEvents="none"
                                p="5px"
                                opacity="0"
                                rounded="0"
                                roundedTop="xs"
                                type={iconRemove}
                                key="clear"
                                {...iconProps}
                            />
                        )
                );
            }
            return <HStack { ...wrapperProps }>{icons}</HStack>;
        }

        if (this.isInternal(item)) {
            return icons;
        }

        if (item && canEdit && !!this.props.onEditRecord) {
            icons.push(
                <IconButton
                    p="5px"
                    rounded="0"
                    alignSelf="center"
                    roundedTop="xs"
                    type={'edit'}
                    key="edit"
                    iconProps={editIconProps}
                    onClick={() => this.props.onEditRecord(item[this.props.valueKey])}
                    {...iconProps}
                    colorScheme="primary"
                />
            );
        }

        if (this.isValueEmpty() || !this.props.allowBlank) {
            icons.push(
                <IconButton
                    p="5px"
                    rounded="0"
                    roundedTop="xs"
                    type={iconAdd || iconNotEmpty}
                    key="open"
                    onClick={this.handleClick}
                    colorScheme="primary"
                    {...iconProps}
                    pointerEvents={triggerSelectOnIconClick ? undefined : 'none'}

                />
            );
        } else if (this.props.allowBlank) {
            icons.push(
                <IconButton
                    colorScheme="muted"
                    p="5px"
                    rounded="0"
                    roundedTop="xs"
                    type={iconRemove}
                    key="clear"
                    onClick={() => this.unselectItem(item)}
                    {...iconProps}
                    pointerEvents={triggerSelectOnIconClick ? undefined : 'none'}
                />
            );
        }

        if (orderable && this.getSelections(true).length > 1) {
            icons.push(
                <SortableHandle key="drag" style={{ display: 'flex' }}>
                    <Icon
                        colorScheme="muted"
                        p="5px"
                        rounded="0"
                        roundedTop="xs"
                        type={iconDragIndicator}
                        className="dragHandle"
                        {...iconProps}
                    />
                </SortableHandle>
            );
        }

        return <HStack { ...wrapperProps }>{icons}</HStack>;
    }

    /**
     * 
     * @param {KeyboardEvent} e 
     */
    onKeyboardEvent = (e) => {
        const keyCode = e.code;
        if (!["ArrowDown", "ArrowUp", "Enter"].includes(keyCode)) return;

        const {valueKey} = this.props;
        const focusedRecordIndex = this.state.focusedRecordIndex;
        let nextFocusedRecordIndex = focusedRecordIndex;
        let source = this.source;

        if (this.props.multiSelect) {
            source = this.filteredSource;
        } else if (nextFocusedRecordIndex < 0) {
            const firstSelectedItem = this.getSelections()[0];
            nextFocusedRecordIndex = !firstSelectedItem ? 0 : source.indexOf((record) => firstSelectedItem[valueKey] === record[valueKey])
        }

        source = this.renderListHeader().concat(source);


        if (!source.some((record) => !record.disabled)) {
            this.setNextFocusedRecordIndex(-1);
            return;
        };



        e.stopImmediatePropagation();


        if (keyCode === 'Enter') {
            if (focusedRecordIndex >= 0) this.handleSelect(source[focusedRecordIndex][valueKey], e)
            return;
        }

        
        
        const offset = keyCode === 'ArrowDown' ? 1 : -1;
        
        nextFocusedRecordIndex = Math.max(focusedRecordIndex, -1);
        while(true) {
            nextFocusedRecordIndex += offset;


            if (source[nextFocusedRecordIndex] && !source[nextFocusedRecordIndex].disabled) {
                break;
            }

            if (nextFocusedRecordIndex < 0) {
                nextFocusedRecordIndex = source.length - 1;
                break;
            }

            if (nextFocusedRecordIndex > source.length - 1) {
                nextFocusedRecordIndex = 0;
                break;
            }

        }

        this.setNextFocusedRecordIndex(nextFocusedRecordIndex);
    }

    /**
     * 
     * @param {number} nextFocusedRecordIndex 
     */
    setNextFocusedRecordIndex = (nextFocusedRecordIndex) => {
        this.setState({ focusedRecordIndex: nextFocusedRecordIndex ?? -1 });
        if (!this.optionsFlatListElem.current) return;
        if (nextFocusedRecordIndex === null) return

        if (nextFocusedRecordIndex < 2) this.optionsFlatListElem.current.getScrollableNode().scrollTo({ y: 0, animated: false });
        else this.optionsFlatListElem.current.scrollToIndex({ index: nextFocusedRecordIndex - 1, animated: false })
    }

    /**
     * 
     * 
     * @param {SourceRecordObject} value 
     * @param {number} index 
     * @param {string} [query] 
     * @returns {import('react').ReactNode}
     * 
     */
    renderOptionLabel (value, _index, query) {
        return renderHtmlAsReact(value[this.props.labelKey], value.disabled ? "" : query)
    }


    renderOptionIcons(item) {
        if (item?.disabled) return null;
        if (this.isInternal(item)) return null;
        
        if (!this.props.multiSelect) {
            return this.props.value !== item[this.props.valueKey] ? null : <Icon size={5} ml="auto" type="radio-button-checked" color="primary.500" />
        }
        
        const props = !this.isSelected(item)
            ? {type: "checkbox-blank-outline" }
            : {type: "checkbox-marked", color: "primary.500"};

        return <Icon size={5} ml="auto" iconFamily="material-community" {...props} />
    }



    /**
     * 
     * 
     * @param {SourceRecordObject} value 
     * @param {number} index 
     * @param {object} initialProps
     * @returns {import('react').ReactNode}
     * 
     */
    getOptionProps (item, _index, initialProps) {
        return {
            ...(item.style ?? {}),
            ...initialProps
        }
    }

    onAddNewRecord = (e) => {
        this.props.onAddNewRecord(e);
        this.close("adding_new_record");
    }

    renderListHeader = () => {
        const {onAddNewRecord, addNewRecordLabel, labelKey, valueKey} = this.props;
        if (!onAddNewRecord) return [];
        if (this.props.disabled) return [];

        return [{
            onClick: this.onAddNewRecord,
            [labelKey]: <Button icon="add" py="0" px="0" iconSize="sm" primary pointerEvents="none">{renderHtmlAsReact(addNewRecordLabel)}</Button>,
            [valueKey]: 'system_add_new_record',
            sticky: true,
            style: {
                backgroundColor: "primary.500:alpha.20",
                _hover: {
                    backgroundColor: "primary.500:alpha.30",
                },
            },
            containerProps: {
                zIndex: 10,
                backgroundColor: "white",
            }
        }];
    }

    renderOption (item, index, query) {
        return (hoverState) => {
            const label = this.renderOptionLabel(item, index, query);
            const icons = this.renderOptionIcons(item, index, query);

            return (
                [
                    <React.Fragment key="optionLabel">{typeof label === 'function' ? label(hoverState) : label}</React.Fragment>,
                    <React.Fragment key="optionIcon">{typeof icons === 'function' ? icons(hoverState) : icons}</React.Fragment>,
                ]
            )
        }
    }

    updatePositions = () => {};

    renderItem ({ item, index }) {
        const optionProps = this.getOptionProps(item, index, {
            disabled: item.disabled,
            borderTopWidth: index === 0 ? 0 : 1,
            onClick: (e) => this.handleSelect(item[this.props.valueKey], e),
        });


        if (this.state.focusedRecordIndex === index) {
            optionProps.bg = 'coolGray.200';
        }
        
        return (
            <DropdownOption {...optionProps}>
                {this.renderOption(item, index, this.query)}
            </DropdownOption>
        );
    }

    __renderItem = (...args) => this.renderItem(...args)

    keyExtractor = (item, index) => {
        return  index;
    }

    getOptions() {
        let { source, query, props } = this,
            { valueKey, labelKey, multiSelect, minQueryLength, allowFiltering } = props;

        // if (allowFiltering && minQueryLength) {
        //     if (query.length < minQueryLength) {
        //         return [
        //             {
        //                 [labelKey]: `Please enter at least ${minQueryLength - query.length} more characters.`,
        //                 [valueKey]: 'system_minimum_chars',
        //                 disabled: true,
        //             },
        //         ];
        //     }
        // }


        if (multiSelect) {
            source = this.filteredSource;
        }


        if (source.filter((item) => "system_empty" === item.value).length) {
            return [...source, ...this.renderListHeader()]
        }


        return this.renderListHeader().concat(source);

    }

    
    renderOptions({ orderable = false } = {}) {

        const source = this.getOptions();

        const list = (
            <FlatList
                data={source}
                ref={this.setOptionsFlatListRef}
                initialNumToRender={this.props.initialNumToRender}
                disableVirtualization={this.props.disableVirtualization}
                CellRendererComponent={this.props.onAddNewRecord ? CellRendererComponent : undefined}
                onLayout={this.updatePositions}
                renderItem={this.__renderItem}
                keyExtractor={this.keyExtractor}
                contentContainerStyle={{ maxHeight: '49vh' }}
            />
        )

        if (!orderable) return list;
        return this.renderSortableList(list);
    }

    /**
     * 
        * @typedef ActionsObject
        * @type {object}
        * @property { (e: FocusEvent<HTMLInputElement>) => void } [handleBlur]
        * @property { (e: FocusEvent<HTMLInputElement>) => void } [handleFocus]
        * @property { (e: ClickEvent<HTMLInputElement>) => void } [handleClick]

     * 
     * @param {*} inputProps 
     * @param { ActionsObject } [actions={}]
     */
    getListItemElement(inputProps, actions = {}) {
        return listItemFactory(Input, this.props, inputProps, {
            renderIcons: this.renderIcons,
            renderTemplateValue: this.renderTemplateValue,
            ...actions
        });
    }

    maybeOrderableListItem = ({ orderable, index, disabled, ...props }) => {
        const key = `item-${index}`;
        if (!orderable) return <>{props.children}</>
        return (
            <SortableItem key={key} index={index} disabled={disabled}>
                <Box>{props.children}</Box>
            </SortableItem>
        );
    };

    renderSelectedItem = (renderListItem, orderable, selected, i) => {
        return (
            this.maybeOrderableListItem({
                index: i,
                orderable,
                children: renderListItem({value: selected, index: i })
            })
        );
    };



    handleReorderItems(oldIndex, newIndex) {
        let valueKey = this.props.valueKey;
        const values = arrayMove(this.getSelections().filter(Boolean), oldIndex, newIndex).map((item) => get(item, valueKey));
        this.props.onChange(values);
    };


    selectedItemsListNode = React.createRef(null);
    setSelectedItemsListNode = (node) => {
        this.selectedItemsListNode = node;
    };

    getDropdownDragContainer = () => {
        return this.selectedItemsListNode;
    };

    setWidth = (event) => {
        const { width } = event.nativeEvent.layout;
        this.setState({ maxWidth: width });
    };

    focusStyle = { borderBottomWidth: 2, borderBottomColor: 'primary.500' };

    renderSortableList = (list) => {
        return (
            <Sortable
                useDragHandle
                getContainer={this.getDropdownDragContainer}
                onSortEnd={this.handleReorderItems.bind(this)}
                axis="y"
            >
                <Box ref={this.setSelectedItemsListNode}>{list}</Box>
            </Sortable>
        )
    }

    renderSelections(triggerProps, inputProps) {
        let selectedItems = this.getSelections(true);
        let { valueKey, orderable } = this.props;

        orderable = orderable && selectedItems.length > 1;
        const listItem = this.getListItemElement(inputProps);

        
        const list = (
            <Box {...triggerProps} borderBottomWidth={1} borderBottomColor="gray.300" _focus={this.focusStyle} onLayout={this.setWidth}>
                <FlatList
                    maxHeight="40vh"
                    ref={this.selectionsFlatListElem}
                    data={selectedItems}
                    renderItem={({ item, index }) => this.renderSelectedItem(listItem, orderable, item, index)}
                    keyExtractor={(item, index) => {
                        return item?.[valueKey] || index;
                    }}
                />
            </Box>
        );

        if (!(orderable && selectedItems.length > 1)) return list;

        return this.renderSortableList(list);
    }

    isDisabled = () => {
        return this.props.loading || this.props.disabled;
    };

    renderDropdownInterface = (props) => {
        const {
            allowBlank,
            auto,
            labelKey,
            required,
            onChange,
            onFocus,
            onBlur, // eslint-disable-line no-unused-vars
            source,
            template,
            minCharacters,
            multiSelect,
            minQueryLength,
            loading,
            filterHint,
            allowFiltering,
            onQueryChange,
            query,
            small,
            valueKey,
            optionClassName,
            label,
            tooltip,
            error,
            disabled,
            ...others
        } = this.props;

        return this.renderSelections(props, { disabled, ...others });
    };

    
    render() {
        return null;
    }
}